import { useCallback, useEffect, useRef, type RefObject } from 'react';
import { enhanceFaqLinkPlaceholders } from './faqLinkPlaceholderDom';
import { navigateFromFaqHash, updateFaqHash } from './faqHashNavigation';

interface UseFaqViewNavigationOptions {
  rootRef: RefObject<HTMLDivElement | null>;
  refreshKey: unknown;
  onExpandGroup: (group: string) => void;
}

export function useFaqViewNavigation({
  rootRef,
  refreshKey,
  onExpandGroup,
}: UseFaqViewNavigationOptions) {
  const suppressNextHashNavigate = useRef(false);

  const navigateFromHash = useCallback(
    async (opts?: { smooth?: boolean }) => {
      await navigateFromFaqHash(onExpandGroup, opts);
    },
    [onExpandGroup],
  );

  useEffect(() => {
    const handler = (event: Event) => {
      const customEvent = event as CustomEvent<{ group: string; question?: string }>;
      const { group, question } = customEvent.detail || {};
      if (!group) {
        return;
      }
      updateFaqHash(group, question);
    };

    window.addEventListener('faq-link-click', handler as EventListener);
    return () => window.removeEventListener('faq-link-click', handler as EventListener);
  }, []);

  useEffect(() => {
    const root = rootRef.current;
    if (!root) {
      return;
    }
    enhanceFaqLinkPlaceholders(root);
  }, [rootRef, refreshKey]);

  useEffect(() => {
    void navigateFromHash({ smooth: false });
    const onHash = () => {
      if (suppressNextHashNavigate.current) {
        suppressNextHashNavigate.current = false;
        return;
      }
      void navigateFromHash({ smooth: true });
    };
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, [navigateFromHash]);

  return { suppressNextHashNavigate };
}
